import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Aviso } from '@/components/ui/aviso';
import { Etiqueta } from '@/components/ui/etiqueta';
import { formatoPrecio, sufijoDeAlcance } from './barra-de-rangos';
import { formatoFecha } from '@/lib/utils/formato-precio';
import { tituloDeSalida } from '../lib/salidas-sin-precio';
import {
  cuentaDelPrecio,
  NOTA_SIN_RANGOS_PROPUESTA,
  NOTA_SIN_REDONDEO_FILA,
  NOTA_SIN_REDONDEO_PROPUESTA,
  REFERENCIA,
  type RecorridoDelPrecio,
  type Sentido,
  type Tiempo,
} from '../lib/recorrido-del-precio';
import {
  lecturaDelPrecio,
  tituloDelPrecio,
  type ConteosDeLaPrueba,
  type Trozo,
} from '../lib/lectura-del-precio';
import {
  dibujoDelPrecio,
  esDelMercado,
  type ClaveDeLeyenda,
  type ClaveDeMarca,
  type DibujoDelPrecio,
  type MarcaDelDibujo,
} from '../lib/dibujo-del-precio';
import { EnElMercado } from './en-el-mercado';

/**
 * «Por qué este precio»: la explicación única, escrita para quien decide
 * (spec 227; maqueta `vaultProgress/maquetas-225/explicacion-v2.html`). El
 * orden: el título, el resultado, el porqué en una frase, los avisos, las
 * opciones (sin precio posible), el dibujo y, cerrada, la cuenta paso a paso.
 * No decide nada: todo sale de `lib/lectura-del-precio.ts`,
 * `lib/dibujo-del-precio.ts` y `cuentaDelPrecio`.
 *
 * `antes`: lo propio de cada pantalla (las reglas rotas y el aviso de conflicto
 * de `ReglasHoyDelProducto`); va debajo del título. `conteos`: en «¿Y si…?»,
 * cuántos productos afecta la regla que se prueba. `sinOpciones`: la pantalla
 * dibuja su propia lista para elegir (la ventana «Decidir» de Por decidir), así
 * que la explicación no repite las opciones.
 */

const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));
/** Alto de una fila de rótulos del dibujo: dos renglones de `text-xs` (nombre y valor). */
const ALTO_FILA = 32;

const colorDe = (s: Sentido) =>
  s === 'SUBE' ? 'text-ok' : s === 'BAJA' ? 'text-baja' : 'text-muted-foreground';

/**
 * Decisión 3 de la maqueta del pulido: las opciones que haya ocupan todo el
 * ancho (dos columnas con dos, tres con tres). Con cuatro, dos filas de dos (tres
 * y una dejaría la segunda fila casi vacía: auditoría I3); con cinco o más, tres
 * por fila. Clases enteras, para que Tailwind las vea.
 */
const columnasPara = (n: number) =>
  n <= 1 ? 'grid-cols-1' : n === 2 || n === 4 ? 'grid-cols-2' : 'grid-cols-3';

export function PorQueEstePrecio({
  recorrido: r,
  tiempo,
  antes,
  alcancePorRegla = {},
  conteos = null,
  sinOpciones = false,
  fecha,
}: {
  recorrido: RecorridoDelPrecio;
  tiempo: Tiempo;
  antes?: ReactNode;
  /** El alcance al lado del nombre de cada regla, en la cuenta. */
  alcancePorRegla?: Record<string, string>;
  /** «¿Y si…?»: lo que la pantalla ya contó sobre el catálogo. */
  conteos?: ConteosDeLaPrueba | null;
  /** No dibujar las opciones sin precio posible: la pantalla muestra las suyas. */
  sinOpciones?: boolean;
  /** La fecha de la propuesta (ISO), junto al título de una de antes: «26 ago». */
  fecha?: string | null;
}) {
  const titulo = tituloDelPrecio(r, tiempo, conteos);
  const lectura = lecturaDelPrecio(r, tiempo, { sinOpciones });
  const cuenta = cuentaDelPrecio(r, tiempo);
  const dibujo = dibujoDelPrecio(r, tiempo);
  const enPrueba = r.reglas?.find((x) => x.probando)?.ruleId ?? null;

  return (
    <section
      aria-label={titulo.nombre}
      data-testid="por-que-este-precio"
      className="space-y-3 rounded-lg border bg-background p-3 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{titulo.texto}</h3>
        {titulo.etiquetas.map((e) => (
          <Etiqueta key={e.texto} tono={e.tono} data-testid="etiqueta-del-titulo">
            {e.texto}
          </Etiqueta>
        ))}
        {fecha && (
          <Etiqueta tono="neutro" data-testid="etiqueta-del-titulo">
            {formatoFecha(fecha)}
          </Etiqueta>
        )}
      </div>
      {antes}

      {r.estado === 'SIN_CALCULO' ? (
        <div className="space-y-1">
          <p>{r.porQue}</p>
          <p className="text-xs text-muted-foreground" data-testid="hoy-y-costo">
            {conMayuscula(REFERENCIA[tiempo])} {fmt(r.hoy)} · costo{' '}
            {r.costo == null ? '— (sin costo cargado)' : fmt(r.costo)}
          </p>
        </div>
      ) : (
        <>
          {lectura.resultado && (
            <div
              data-testid="resultado"
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5"
            >
              <span className="text-2xl font-semibold tabular-nums">
                {lectura.resultado.prefijo && (
                  <span className="mr-1.5 text-base font-medium text-muted-foreground">
                    {lectura.resultado.prefijo}
                  </span>
                )}
                {lectura.resultado.precio}
              </span>
              <span className="flex flex-wrap gap-1.5">
                {lectura.resultado.datos.map((d) => (
                  <Etiqueta key={d.clave} tono={d.tono} data-testid="dato" data-clave={d.clave}>
                    {d.texto}
                  </Etiqueta>
                ))}
              </span>
            </div>
          )}

          {lectura.porque.length > 0 && (
            <p data-testid="porque" className="max-w-prose leading-relaxed text-foreground/80">
              <Frase trozos={lectura.porque} />
            </p>
          )}

          {lectura.avisos.map((a) => (
            <Aviso
              key={a.clave}
              tono={a.tono}
              titulo={a.titulo}
              data-testid="aviso-del-precio"
              data-clave={a.clave}
            >
              {a.texto}
            </Aviso>
          ))}

          {/* Ronda final (A5), maqueta #historial: lo de una propuesta vieja va en un recuadro
              info con el precio de antes, una sola vez, ARRIBA de la cuenta; las demás notas,
              la línea ámbar al final. Se reconoce por la constante, no por cómo empieza el texto. */}
          {avisoDeLoViejo(cuenta.notas, r.hoy)}

          {lectura.opciones && !sinOpciones && (
            <div
              className={cn('grid gap-2', columnasPara(lectura.opciones.length))}
              data-testid="opciones"
            >
              {lectura.opciones.map((o) => (
                <div
                  key={o.clave}
                  data-testid="salida"
                  data-pierde={o.pierde}
                  className={cn(
                    'rounded-md border p-3',
                    o.pierde && 'border-bloqueo-borde bg-bloqueo-suave',
                    o.elegida && 'border-foreground'
                  )}
                >
                  <div className="flex flex-wrap items-center gap-1.5 font-medium">
                    {o.titulo}
                    {o.elegida && (
                      <Etiqueta tono="ok" data-testid="salida-elegida">
                        la elegida
                      </Etiqueta>
                    )}
                  </div>
                  <div className="my-1 text-lg font-semibold tabular-nums">{o.precio}</div>
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {o.lineas.map((l, i) => (
                      <li
                        key={`${i}-${l.texto}`}
                        className={cn(l.malo && 'font-semibold text-bloqueo')}
                      >
                        {l.texto}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Lote B1 (spec 225, pieza 2): las tiendas que cuentan, junto al dibujo. */}
          {r.mercado && (
            <EnElMercado
              mercado={r.mercado}
              queda={r.queda ?? r.elegida?.precio ?? null}
              alProponer={tiempo === 'PASO'}
            />
          )}

          {dibujo && <Dibujo d={dibujo} />}

          <details data-testid="la-cuenta" className="border-t pt-2">
            <summary className="cursor-pointer text-sm font-medium text-primary">
              Ver la cuenta paso a paso
            </summary>
            <table className="mt-2 w-full text-sm" data-testid="cuenta">
              <tbody>
                {cuenta.pasos.map((p) => {
                  const sufijo =
                    p.regla && p.ruleId
                      ? sufijoDeAlcance(p.regla, alcancePorRegla[p.ruleId])
                      : null;
                  return (
                    <tr key={p.clave} data-testid="paso" className="border-b border-dashed">
                      <td className="py-1 pr-2">
                        {p.regla && (
                          <>
                            <span title={p.regla}>
                              «{p.regla}»
                              {sufijo && <span className="text-muted-foreground"> · {sufijo}</span>}
                            </span>{' '}
                          </>
                        )}
                        {p.marca && (
                          <>
                            <span
                              className={cn(
                                'text-xs',
                                p.marca === 'encendida'
                                  ? 'text-muted-foreground'
                                  : 'rounded bg-primary/15 px-1 font-medium text-primary'
                              )}
                            >
                              {p.marca}
                            </span>{' '}
                          </>
                        )}
                        {p.texto}
                        {p.detalle && <span className="text-muted-foreground"> · {p.detalle}</span>}
                        {p.manda && (
                          <span
                            data-testid="manda"
                            className="ml-1.5 whitespace-nowrap rounded bg-foreground px-1.5 py-px text-xs font-bold text-background"
                          >
                            decide
                          </span>
                        )}
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap py-1 text-right tabular-nums',
                          p.regla != null && colorDe(p.sentido)
                        )}
                      >
                        {p.valor}
                      </td>
                    </tr>
                  );
                })}
                {cuenta.final && (
                  <tr data-testid="paso-final" className="font-semibold">
                    <td className="py-1 pr-2">
                      {cuenta.final.texto}{' '}
                      <span className="font-normal text-muted-foreground">
                        {cuenta.final.salida &&
                          `· se eligió «${tituloDeSalida(cuenta.final.salida, r.reglas, enPrueba)}» `}
                        · {cuenta.final.detalle}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-1 text-right tabular-nums">
                      {cuenta.final.valor}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </details>

          {cuenta.notas
            .filter((n) => !notasDelAviso(cuenta.notas).includes(n))
            .map((n) => (
              <p key={n} data-testid="nota" className="text-xs text-alerta">
                {n}
              </p>
            ))}
        </>
      )}
    </section>
  );
}

const conMayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Las notas de «no guardó el redondeo»: la de la propuesta y la de la fila. */
const NOTAS_DE_LO_VIEJO: readonly string[] = [
  NOTA_SIN_REDONDEO_PROPUESTA,
  NOTA_SIN_RANGOS_PROPUESTA,
  NOTA_SIN_REDONDEO_FILA,
];

/**
 * Las notas que van en el recuadro: sólo si llega la de la propuesta (la de la fila sola
 * es de una fila de hoy y sigue siendo la línea ámbar); con ella, la de la fila se suma.
 */
const notasDelAviso = (notas: string[]) =>
  notas.includes(NOTA_SIN_REDONDEO_PROPUESTA) || notas.includes(NOTA_SIN_RANGOS_PROPUESTA)
    ? notas.filter((n) => NOTAS_DE_LO_VIEJO.includes(n))
    : [];

/**
 * El recuadro de «esta propuesta es de antes». Si llegan las dos notas dice lo que
 * no se guardó una sola vez; sin precio de antes (`hoy` null) omite «Antes X.»:
 * «Antes —.» sería un dato inventado.
 */
function avisoDeLoViejo(notas: string[], hoy: number | null) {
  const hay = notasDelAviso(notas);
  if (hay.length === 0) return null;
  const que = [
    ...new Set(
      hay.map((n) => conMayuscula(n.replace(/^(Esta propuesta|Este cálculo) es de antes: /, '')))
    ),
  ].join(' ');
  return (
    <Aviso tono="info" titulo="Esta propuesta es de antes" data-testid="nota">
      {que}
      {hoy != null && ` Antes ${fmt(hoy)}.`}
    </Aviso>
  );
}

/** El porqué: los números en negrita. */
function Frase({ trozos }: { trozos: Trozo[] }) {
  return (
    <>
      {trozos.map((x, i) =>
        x.negrita ? (
          <b key={i} className="font-semibold text-foreground">
            {x.texto}
          </b>
        ) : (
          <Fragment key={i}>{x.texto}</Fragment>
        )
      )}
    </>
  );
}

/** Debajo del costo: rayado, con el color del costo. */
const RAYADO: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, hsl(var(--marca-costo) / 0.35) 0 4px, transparent 4px 8px)',
};

/** Cómo se ubica el rótulo respecto de su marca (`alinea`, auditoría I4). */
const ALINEA: Record<MarcaDelDibujo['alinea'], string> = {
  IZQUIERDA: 'translate-x-0 text-left',
  CENTRO: '-translate-x-1/2 text-center',
  DERECHA: '-translate-x-full text-right',
};

const COLOR: Record<Exclude<ClaveDeMarca, `tienda-${number}`>, { raya: string; texto: string }> = {
  pide: { raya: 'w-0.5 bg-marca-pide', texto: 'text-marca-pide' },
  hoy: { raya: 'w-0.5 bg-marca-hoy', texto: 'text-marca-hoy' },
  queda: { raya: 'w-[3px] bg-foreground', texto: 'text-foreground' },
  costo: { raya: 'w-0.5 bg-marca-costo', texto: 'text-marca-costo' },
  masBarato: { raya: '', texto: 'text-marca-mercado' },
  conReglasDeHoy: { raya: 'w-0.5 bg-marca-hoy', texto: 'text-marca-hoy' },
  piso: { raya: 'w-0.5 bg-ok', texto: 'text-ok' },
  techo: { raya: 'w-0.5 bg-primary', texto: 'text-primary' },
  minNueva: { raya: 'w-0.5 bg-primary', texto: 'text-primary' },
  maxNueva: { raya: 'w-0.5 bg-primary', texto: 'text-primary' },
};

/** El color de una marca; las tiendas (pieza 2) llevan el del mercado. */
const colorDeLaMarca = (c: ClaveDeMarca) =>
  c.startsWith('tienda-') ? COLOR.masBarato : COLOR[c as keyof typeof COLOR];

const MUESTRA: Record<Exclude<ClaveDeLeyenda, 'perdida' | 'masBarato'>, string> = {
  verde: 'bg-rango',
  hueco: 'bg-bloqueo',
  carrilPiso: 'bg-rango/75',
  carrilTecho: 'bg-primary/70',
  flecha: 'bg-marca-pide',
};

function Muestra({ clave }: { clave: ClaveDeLeyenda }) {
  if (clave === 'perdida')
    return <i aria-hidden className="inline-block h-2 w-3.5 rounded-sm" style={RAYADO} />;
  if (clave === 'masBarato')
    return <i aria-hidden className="inline-block h-2 w-2 rotate-45 bg-marca-mercado" />;
  return <i aria-hidden className={cn('inline-block h-2 w-3.5 rounded-sm', MUESTRA[clave])} />;
}

/**
 * El dibujo (spec 227 §6): las marcas con nombre y valor en filas que no se
 * pisan, lo verde, lo rayado a pérdida, la flecha con punta, el hueco con su
 * rótulo y sus dos carriles, y la leyenda de lo que se ve.
 */
export function Dibujo({ d }: { d: DibujoDelPrecio }) {
  const pos = (v: number) => `${((v - d.desde) / (d.hasta - d.desde)) * 100}%`;
  const tramo = (min: number | null, max: number | null): CSSProperties => ({
    left: min == null ? '0%' : pos(min),
    ...(max == null
      ? { right: 0 }
      : { width: `calc(${pos(max)} - ${min == null ? '0%' : pos(min)})` }),
  });
  const filas = (lado: 'ARRIBA' | 'ABAJO') =>
    Math.max(0, ...d.marcas.filter((m) => m.lado === lado).map((m) => m.fila + 1));
  const filasArriba = filas('ARRIBA');
  const filasAbajo = filas('ABAJO');
  const flecha = d.flecha;
  const sube = flecha != null && flecha.a > flecha.de;

  return (
    <div data-testid="dibujo" className="space-y-2">
      <div
        className="relative mx-1.5"
        style={{
          paddingTop: filasArriba * ALTO_FILA + 8,
          paddingBottom: filasAbajo * ALTO_FILA + (d.hueco ? 44 : 8),
        }}
      >
        <div
          className="relative h-3 rounded-full bg-muted"
          role="img"
          aria-label={`el recorrido del precio: ${d.enPalabras}`}
        >
          {d.perdida != null && (
            <div
              data-testid="zona-perdida"
              className="absolute inset-y-0 left-0 rounded-l-full"
              style={{ width: pos(d.perdida), ...RAYADO }}
            />
          )}
          {d.verde && (
            <div
              data-testid="verde"
              className="absolute inset-y-0 rounded-full bg-rango"
              style={tramo(d.verde.min, d.verde.max)}
            />
          )}
          {d.hueco && (
            <>
              <div
                data-testid="hueco"
                className="absolute inset-y-0 bg-bloqueo ring-2 ring-bloqueo ring-offset-1 ring-offset-background"
                style={tramo(d.hueco.de, d.hueco.a)}
              />
              <div
                data-testid="rotulo-hueco"
                className="absolute -translate-x-1/2 whitespace-nowrap text-center text-xs font-semibold leading-4 text-bloqueo"
                style={{
                  left: pos((d.hueco.de + d.hueco.a) / 2),
                  top: `calc(100% + ${8 + filasAbajo * ALTO_FILA}px)`,
                }}
              >
                <span className="block">el hueco: ningún precio</span>
                <span className="block">cumple {d.cualesCumplir ?? 'las dos'}</span>
              </div>
            </>
          )}
          {flecha && (
            <div
              data-testid="flecha"
              data-sentido={sube ? 'SUBE' : 'BAJA'}
              className="absolute top-1 h-1 bg-marca-pide"
              style={{
                left: pos(Math.min(flecha.de, flecha.a)),
                width: `calc(${pos(Math.max(flecha.de, flecha.a))} - ${pos(Math.min(flecha.de, flecha.a))})`,
              }}
            >
              <span
                data-testid="punta"
                aria-hidden
                className={cn(
                  'absolute -top-1 h-0 w-0 border-y-[6px] border-y-transparent',
                  sube
                    ? '-right-2 border-l-8 border-l-marca-pide'
                    : '-left-2 border-r-8 border-r-marca-pide'
                )}
              />
            </div>
          )}
          {d.marcas.map((m) => {
            const lejos = `calc(100% + ${6 + m.fila * ALTO_FILA}px)`;
            return (
              <div key={m.clave}>
                {esDelMercado(m.clave) ? (
                  <div
                    data-testid="rombo"
                    className="absolute top-0.5 h-2.5 w-2.5 -translate-x-1/2 rotate-45 bg-marca-mercado"
                    style={{ left: pos(m.valor) }}
                  />
                ) : (
                  <div
                    className={cn('absolute -translate-x-1/2', colorDeLaMarca(m.clave).raya)}
                    style={{
                      left: pos(m.valor),
                      height: 18 + m.fila * ALTO_FILA,
                      ...(m.lado === 'ARRIBA' ? { bottom: 0 } : { top: 0 }),
                    }}
                  />
                )}
                <div
                  data-testid={`rotulo-${m.clave}`}
                  data-fila={m.fila}
                  data-alinea={m.alinea}
                  className={cn(
                    'absolute whitespace-nowrap text-xs leading-4',
                    ALINEA[m.alinea],
                    colorDeLaMarca(m.clave).texto
                  )}
                  style={{
                    left: pos(m.valor),
                    ...(m.lado === 'ARRIBA' ? { bottom: lejos } : { top: lejos }),
                  }}
                >
                  <span className="block font-semibold">{m.nombre}</span>
                  <span className="block tabular-nums text-muted-foreground">{fmt(m.valor)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {d.carriles.length > 0 && (
        <div data-testid="carriles" className="mx-1.5 space-y-1.5">
          {d.carriles.map((c) => (
            <div key={c.clave} data-testid={`carril-${c.clave}`}>
              <div className="text-xs text-foreground/80">{c.titulo}</div>
              <div className="relative mt-0.5 h-2.5 rounded-full bg-muted">
                <div
                  className={cn(
                    'absolute inset-y-0 rounded-full',
                    c.clave === 'piso' ? 'bg-rango/75' : 'bg-primary/70'
                  )}
                  style={tramo(c.min, c.max)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        data-testid="leyenda"
        className="mx-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
      >
        {d.leyenda.map((l) => (
          <span key={l.clave} data-clave={l.clave} className="inline-flex items-center gap-1.5">
            <Muestra clave={l.clave} />
            {l.texto}
          </span>
        ))}
      </div>
    </div>
  );
}
